import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { initGtag, grantConsent } from '../lib/analytics';

const KEY = 'dd-cookie-consent';

export const CookieConsent = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    initGtag();
    if (localStorage.getItem(KEY) === 'accepted') {
      grantConsent();
      return undefined;
    }
    if (!localStorage.getItem(KEY)) {
      const id = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  useEffect(() => {
    document.body.classList.toggle('cookie-open', visible);
    return () => document.body.classList.remove('cookie-open');
  }, [visible]);

  const choose = (val) => {
    localStorage.setItem(KEY, val);
    if (val === 'accepted') grantConsent();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[70] md:bottom-6 md:left-6 md:right-auto md:w-[min(92vw,520px)]"
        >
          <div className="bg-surface border border-line/15 md:rounded-2xl shadow-[0_24px_70px_rgba(24,26,32,0.18)] backdrop-blur-xl px-5 py-4 md:px-6 md:py-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 w-9 h-9 rounded-full bg-accent/10 text-accent grid place-items-center">
                <Cookie size={18} />
              </span>
              <div className="min-w-0">
                <p className="font-display font-medium text-sm md:text-base">{t('cookies.title')}</p>
                <p className="text-smoke text-xs md:text-sm leading-relaxed mt-1">
                  {t('cookies.text')}{' '}
                  <Link to="/politica-cookies" data-testid="cookie-policy-link" className="text-accent underline underline-offset-2 hover:text-accenth">
                    {t('cookies.policy')}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                data-testid="cookie-accept-button"
                onClick={() => choose('accepted')}
                className="flex-1 bg-accent text-white text-xs font-bold uppercase tracking-[0.15em] px-5 py-3 rounded-full hover:bg-accenth transition-colors duration-300"
              >
                {t('cookies.accept')}
              </button>
              <button
                data-testid="cookie-refuse-button"
                onClick={() => choose('rejected')}
                className="flex-1 border border-line/20 text-ink text-xs font-bold uppercase tracking-[0.15em] px-5 py-3 rounded-full hover:border-ink transition-colors duration-300"
              >
                {t('cookies.refuse')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
